/**
 * Единицы (§34, §51).
 *
 * Новая цена действует на новые расчёты. Уже посчитанные проекты не
 * пересчитываются: показанная пользователю цена — это обещание (§36).
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminApi } from '../../api/admin';
import { Alert } from '../../components/ui/Alert';
import { Button } from '../../components/ui/Button';
import { formatDateTime } from '../../i18n/format';
import { useTranslation } from 'react-i18next';
import { errorText } from '../../i18n/errors';

export function AdminPricesPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [reason, setReason] = useState('');

  const prices = useQuery({ queryKey: ['admin', 'prices'], queryFn: adminApi.prices });

  const update = useMutation({
    mutationFn: (params: { code: string; units: number }) =>
      adminApi.updatePrice(params.code, params.units, reason || undefined),
    onSuccess: async (_result, params) => {
      setDrafts((current) => {
        const next = { ...current };
        delete next[params.code];
        return next;
      });
      await queryClient.invalidateQueries({ queryKey: ['admin', 'prices'] });
    },
  });

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-2">
        <input
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          placeholder={t('admin.price.reasonPlaceholder')}
          className="h-9 w-96 rounded-control border border-line bg-surface px-3 text-meta text-content placeholder:text-content-muted focus:border-accent focus:outline-none"
        />
        <span className="text-meta text-content-muted">{t('admin.price.note')}</span>
      </div>

      {update.isError ? (
        <Alert tone="error">{errorText(update.error)}</Alert>
      ) : null}

      <div className="overflow-x-auto rounded-card border border-line">
        <table className="w-full border-collapse text-meta">
          <thead>
            <tr className="border-b border-line bg-surface-secondary text-left text-content-secondary">
              <th className="px-3 py-2 font-medium">{t('admin.price.code')}</th>
              <th className="px-3 py-2 font-medium">{t('common.name')}</th>
              <th className="px-3 py-2 font-medium">{t('admin.price.basis')}</th>
              <th className="px-3 py-2 text-right font-medium">{t('admin.price.units')}</th>
              <th className="px-3 py-2 font-medium">{t('admin.price.changed')}</th>
              <th className="px-3 py-2 font-medium" />
            </tr>
          </thead>
          <tbody>
            {(prices.data ?? []).map((row) => {
              const draft = drafts[row.code] ?? String(row.units);
              const changed = Number(draft) !== row.units && draft !== '';

              return (
                <tr key={row.code} className="border-b border-line bg-surface last:border-0">
                  <td className="px-3 py-2 text-content-secondary">{row.code}</td>
                  <td className="px-3 py-2 text-content">{row.title}</td>
                  <td className="px-3 py-2 text-content-secondary">
                    {row.unitBasis === 'per_second' ? t('admin.price.perSecond') : t('admin.price.perOperation')}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <input
                      type="number"
                      value={draft}
                      onChange={(event) =>
                        setDrafts((current) => ({ ...current, [row.code]: event.target.value }))
                      }
                      className="h-7 w-20 rounded-control border border-line bg-surface-secondary px-2 text-right text-meta text-content focus:border-accent focus:outline-none"
                    />
                  </td>
                  <td className="px-3 py-2 text-content-muted">
                    {formatDateTime(row.updatedAt)}
                  </td>
                  <td className="px-3 py-2">
                    {changed ? (
                      <Button
                        variant="secondary"
                        disabled={update.isPending}
                        onClick={() => update.mutate({ code: row.code, units: Number(draft) })}
                      >{t('common.save')}</Button>
                    ) : null}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
